// apps/client/src/contexts/NotificationsContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Notification } from '@avanzu/types';
import { useAuth } from './AuthContext';
import { apiClient } from '../services/apiClient';

interface NotificationsContextType {
  notifications: Notification[];
  latestNotification: Notification | null;
  notificationCount: number;
  loading: boolean;
  dismissNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, currentOrganisation } = useAuth();

  // Filter notifications based on current organisation context
  const notifications = useMemo(() => {
    if (!currentOrganisation) {
      // If no current organisation, only show global notifications (organisationId = null/undefined)
      return allNotifications.filter((notification) => !notification.organisationId);
    }

    // Filter for current organisation OR global notifications
    return allNotifications.filter(
      (notification) =>
        notification.organisationId === currentOrganisation.id || !notification.organisationId
    );
  }, [allNotifications, currentOrganisation]);

  // Get the most recent notification from filtered list
  const latestNotification = notifications.length > 0 ? notifications[0] : null;
  const notificationCount = notifications.length;

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setAllNotifications([]);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setAllNotifications([]);
        return;
      }

      // Fetch ALL notifications for the user (we'll filter client-side)
      const response = await apiClient.get(`/user/notifications`);

      if (response.ok) {
        const data = await response.json();
        setAllNotifications(data.notifications || []);
      } else if (response.status === 401) {
        // Token expired or invalid, clear notifications
        setAllNotifications([]);
      } else {
        console.error('Failed to fetch notifications:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const dismissNotification = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        // Remove from local state immediately
        setAllNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      } else {
        console.error('Failed to dismiss notification:', response.statusText);
      }
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Fetch notifications when user changes or component mounts
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Periodic polling every 60 seconds when user is logged in
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Refresh when user comes back to the tab (visibility change)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, fetchNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        latestNotification,
        notificationCount,
        loading,
        dismissNotification,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
